import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CreateChatModel } from '../../../../core/models/create-chat-model';
import { ChatService } from '../../../../core/services/chat/chat.service';
import { environment } from '../../../../../environments/environment';
import { SignalRService } from '../../../../core/services/signalR/signalR';
import { ChatListModel } from '../../../../core/models/chat/chat-list.model';
import { UserSessionModel } from '../../../../core/models/user/current-user.model';

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
})
export class ChatComponent {
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('chatContainer') private chatContainer!: ElementRef;
  @Input() currentUser: UserSessionModel | null = null;
  @Input() patientHeaderInformations: any = null;
  @Input() consultationId: string = '';
  @Output() patientInfoRequested = new EventEmitter<string>();

  chatList: ChatListModel[] = [];
  messageText: string = '';
  fileCount: number = 0;
  file: File | null = null;

  constructor(
    private translateService: TranslateService,
    private chatService: ChatService,
    private signalRService: SignalRService
  ) {
    this.createChatSignalRConnection();
  }

  ngOnInit() {
    if (this.consultationId) {
      this.getChatList(this.consultationId);
    }
  }

  protected async createChatSignalRConnection() {
    const signalRBasePath = `${environment.apiLocations.chat}`.split(
      '/api/v1'
    )[0];
    const hubUrl = signalRBasePath + '/chat-hub';
    this.signalRService.connect(hubUrl);
    this.signalRService.on('receiveChatAdded', (message) => {
      const parsedJson = JSON.parse(message);
      if (parsedJson.PatientRecordCaseId === this.consultationId) {
        if (parsedJson?.Message) {
          const newMessage: ChatListModel = {
            chatId: parsedJson.ChatId,
            patientRecordCaseId: parsedJson.PatientRecordCaseId,
            createdUserId: parsedJson.CreatedUserId,
            createdUserName: parsedJson.CreatedUserName,
            message: parsedJson.Message,
            chatFiles: parsedJson.ChatFiles || [],
            createdDateTime: parsedJson.CreatedDateTime ?? '',
            relativeTime: parsedJson.RelativeTime ?? '',
          };
          this.chatList = [...this.chatList, newMessage];
          setTimeout(() => {
            this.scrollToBottom();
          }, 100);
        }
      }
    });
  }

  getChatList(id: string) {
    this.chatService.chatList(id).subscribe({
      next: (res) => {
        this.chatList = res;
        setTimeout(() => {
          this.scrollToBottom();
        }, 100);
      },
    });
  }

  handleFileClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.fileInput.nativeElement.click();
  }

  handleFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.fileCount = input.files.length;
      this.file = input.files[0];
      input.value = '';
    }
  }

  downloadChatFile(path: string, fileName: string) {
    const base64Data = path?.includes(',') ? path?.split(',')[1] : path;

    const binaryString = window.atob(base64Data);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style.display = 'none';
    a.href = url;
    a.download = fileName;
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  sendChat(id: string): void {
    if (this.messageText.trim() === '') return;

    const createChatModel: CreateChatModel = {
      patientRecordCaseId: id,
      patientName: this.patientHeaderInformations.name,
      patientSurname: this.patientHeaderInformations.lastName,
      patientRecordCaseNumber:
        this.patientHeaderInformations.patientRecordCaseNumber,
      createdUserName: this.currentUser?.name || '',
      message: this.messageText || '',
      expertUserName: this.patientHeaderInformations?.expertUserName || '',
      generalPractitionerUserName:
        this.patientHeaderInformations?.generalPractitionerUserName || '',
      file: this.file || undefined,
    };

    this.chatService.chat(createChatModel).subscribe({
      next: () => {
        this.messageText = '';
        this.file = null;
        this.fileCount = null;
        this.patientInfoRequested.emit(id);
      },
    });
  }

  scrollToBottom(): void {
    if (this.chatContainer && this.chatContainer.nativeElement) {
      this.chatContainer.nativeElement.scrollTop =
        this.chatContainer.nativeElement.scrollHeight + 10;
    }
  }
}
